import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import swal from "sweetalert";
import {
  createCircular,
  updateCircular,
} from "../../../services/adminApiService/circularApi/circularApi";

const CircularModal = ({ circular, onClose }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (circular) {
      setTitle(circular.title || "");
      setDescription(circular.description || "");
      setStartDateTime(
        circular.start_date_time
          ? circular.start_date_time.slice(0, 16)
          : ""
      );
      setEndDateTime(
        circular.end_date_time
          ? circular.end_date_time.slice(0, 16)
          : ""
      );
    }
  }, [circular]);

  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    if (!/\.pdf$/i.test(file.name)) {
      swal("Error", "Only PDF files are allowed.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      swal("Error", "File size must be under 10 MB.", "error");
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      swal("Error", "Title is required.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    if (description) formData.append("description", description);
    if (startDateTime) formData.append("start_date_time", startDateTime);
    if (endDateTime) formData.append("end_date_time", endDateTime);
    if (selectedFile) formData.append("attachement", selectedFile);

    setLoading(true);
    try {
      if (circular) {
        await updateCircular(circular.id, formData);
        swal("Success", "Circular updated.", "success");
      } else {
        await createCircular(formData);
        swal("Success", "Circular created.", "success");
      }
      onClose(true);
    } catch {
      swal("Error", "Operation failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show onHide={() => onClose(false)} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {circular ? "Edit Circular" : "Add Circular"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Sports Policy Circular 2024"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the circular..."
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Start Date/Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={startDateTime}
                  onChange={(e) => setStartDateTime(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>End Date/Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={endDateTime}
                  onChange={(e) => setEndDateTime(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-4">
            <Form.Label>
              PDF Attachment{" "}
              {circular && (
                <small className="text-muted ms-2">
                  (Leave empty to keep existing file)
                </small>
              )}
            </Form.Label>
            {circular?.attachement_storage_url && (
              <div className="mb-2">
                <a
                  href={circular.attachement_storage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View current file
                </a>
              </div>
            )}
            <Form.Control
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">PDF only. Max 10 MB.</Form.Text>
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="secondary"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : circular ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CircularModal;
